// Verified ADMALL INDIA Services Data Repository

export const SERVICES = [
  {
    slug: "brand-engineering",
    title: "Brand Engineering",
    code: "Service",
    tagline: "Build the foundation before buying attention.",
    heroImage: "https://images.unsplash.com/photo-1781127445273-0f1fdca3a9f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "A brand is not merely a logo or visual palette—it is the strategic anchor of your commercial growth. We engineer distinctive identities, precise positioning, and unified messaging architectures that make businesses immediately recognizable, trusted, and difficult to substitute.",
    painPoints: [
      "Fragmented visual and verbal identity leading to weak brand recall across digital and physical touchpoints.",
      "Unclear market positioning where prospective customers perceive your offering as an interchangeable commodity.",
      "Sales conversations stalling because your core value proposition is not immediately articulated.",
      "Premature advertising spend directed at an unclear, unconvincing brand foundation."
    ],
    deliverables: [
      {
        title: "Brand Strategy & Positioning",
        desc: "Defining your category, audience perception, and unfair competitive advantage."
      },
      {
        title: "Logo & Visual Identity System",
        desc: "Crafting iconic marks, responsive typography, and cohesive aesthetic rules."
      },
      {
        title: "Messaging Architecture & USP",
        desc: "High-impact taglines, elevator pitches, and core value proposition articulation."
      },
      {
        title: "Corporate Identity & Collateral",
        desc: "Brochures, pitch presentations, stationeries, and corporate communications."
      },
      {
        title: "Brand Guidelines & Governance",
        desc: "Clear design rulebooks preserving consistency across internal and agency teams."
      },
      {
        title: "Campaign Conceptualization",
        desc: "Creative frameworks that translate brand pillars into active marketing themes."
      }
    ],
    strategicInsight:
      "The Brand Engineering framework establishes market clarity first. Before allocating budgets to distribution, we build an unmistakable identity that converts attention into lasting enterprise value.",
    bullets: [
      "Brand strategy",
      "Brand naming",
      "Positioning",
      "USP development",
      "Brand architecture",
      "Logo & visual identity",
      "Brand messaging",
      "Corporate identity",
      "Brand guidelines",
      "Presentation & pitch design",
      "Campaign concepts",
      "AI-assisted brand intelligence"
    ],
    relatedServices: ["multimedia-production", "digital-marketing", "performance-lead-generation"],
    relatedIndustries: ["real-estate", "manufacturing-b2b", "d2c"]
  },
  {
    slug: "multimedia-production",
    title: "Multimedia Production",
    code: "Service",
    tagline: "Turn ideas into communication people can see, hear and remember.",
    heroImage: "https://static.prod-images.emergentagent.com/jobs/c6f51fbc-ba88-4cac-a357-6a477f785856/images/2d69758685be1507adfa52dc83be500f77a6e7a635f1523356c64b1336ead048.jpeg",
    summary:
      "In an era of relentless scrolling, high-fidelity visual production is what separates memorable brands from forgotten noise. From concept development and scripting to filming, editing, and sound design, we produce cinematic media that commands attention and builds emotional conviction.",
    painPoints: [
      "Low-quality, generic video assets that undermine executive credibility and commercial authority.",
      "Creative disconnect between creative scripts and commercial business objectives.",
      "High coordination friction when managing disparate videographers, scriptwriters, and editors.",
      "Social reels that accumulate superficial views without establishing lasting brand affinity or qualified leads."
    ],
    deliverables: [
      {
        title: "Brand & Corporate Films",
        desc: "Cinematic narrative films defining enterprise legacy, operational scale, and vision."
      },
      {
        title: "Commercial Advertisements & TVCs",
        desc: "Broadcast-quality commercials engineered for television, OTT, and digital distribution."
      },
      {
        title: "Product Videos & Demonstrations",
        desc: "Dynamic feature showcases, unboxings, and high-fidelity product demonstrations."
      },
      {
        title: "High-Velocity Short Form & Reels",
        desc: "Platform-optimized vertical video engineered for algorithmic engagement and stop-the-scroll appeal."
      },
      {
        title: "Interviews & Customer Testimonials",
        desc: "Authentic, high-credibility founder and client video storytelling."
      },
      {
        title: "Commercial Studio Photography",
        desc: "Ultra-high-resolution product and lifestyle photography shot in professional studios."
      }
    ],
    strategicInsight:
      "Our in-house production studio handles the entire creative lifecycle—scriptwriting, set design, camera direction, color grading, motion graphics, and audio mixing—ensuring uncompromising visual fidelity.",
    bullets: [
      "Brand films",
      "Corporate films",
      "Advertisement films",
      "Product videos",
      "Promotional films",
      "Reels & short-form video",
      "Interviews & testimonials",
      "Event films",
      "Product photography",
      "Image engineering",
      "Motion graphics",
      "Explainer videos",
      "AI-assisted visual production"
    ],
    relatedServices: ["brand-engineering", "digital-marketing", "ai-powered-marketing"],
    relatedIndustries: ["real-estate", "hospitality", "entertainment"]
  },
  {
    slug: "digital-marketing",
    title: "Digital Marketing",
    code: "Service",
    tagline: "Build visibility where your customers actually search and spend attention.",
    heroImage: "https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200",
    summary:
      "Digital marketing should never be an arbitrary collection of isolated social posts. We build integrated digital footprints that connect search engine dominance, targeted social distribution, and authority content into a cohesive customer acquisition engine.",
    painPoints: [
      "Publishing content consistently without generating measurable inquiries or commercial pipeline.",
      "Poor organic search rankings leaving high-intent Google searches to your competitors.",
      "Inconsistent messaging across fragmented channels (Meta, LinkedIn, Google).",
      "Absence of retargeting funnels, losing 95%+ of one-time website visitors."
    ],
    deliverables: [
      {
        title: "Search Engine Optimization (SEO)",
        desc: "Technical, on-page, and authority SEO engineered to dominate commercial search keywords."
      },
      {
        title: "Meta & LinkedIn Channel Strategy",
        desc: "Curated social presence and targeted advertising mapped to distinct audience stages."
      },
      {
        title: "Google Business Profile & Local Search",
        desc: "Hyper-local search optimization capturing nearby customers ready to transact."
      },
      {
        title: "Content Strategy & Distribution",
        desc: "Thought leadership, long-form articles, and educational collateral that build authority."
      },
      {
        title: "Omnichannel Remarketing",
        desc: "Keeping your brand top-of-mind across the web until prospects are ready to buy."
      },
      {
        title: "Analytics & Conversion Architecture",
        desc: "Clean attribution modeling and dashboard visibility into genuine marketing ROI."
      }
    ],
    strategicInsight:
      "We replace fragmented vendor activity with a single unified strategy: discoverable on search, commanding on social, and persistent through remarketing.",
    bullets: [
      "Social Media Marketing",
      "SEO",
      "Google Search",
      "YouTube",
      "Meta Advertising",
      "LinkedIn Marketing",
      "Content Marketing",
      "Google Business Profile optimisation",
      "Local search visibility",
      "Remarketing",
      "Landing-page strategy",
      "Conversion optimisation"
    ],
    relatedServices: ["performance-lead-generation", "brand-engineering", "multimedia-production"],
    relatedIndustries: ["healthcare", "education", "manufacturing-b2b"]
  },
  {
    slug: "performance-lead-generation",
    title: "Performance & Lead Generation",
    code: "Service",
    tagline: "Attention is not the finish line. Measurable enquiries and sales are.",
    heroImage: "https://images.unsplash.com/photo-1728464864608-98006d66f075?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "Likes and views do not sustain a commercial enterprise. We build disciplined paid acquisition funnels across Google and Meta that turn qualified customer attention into verified phone calls, booked appointments, and high-intent sales enquiries.",
    painPoints: [
      "Wasted ad spend on low-quality leads that your sales team cannot close.",
      "High Cost Per Acquisition (CPA) driven by weak creative or broken landing pages.",
      "Complicated multi-step lead forms where prospects abandon before submitting.",
      "Zero integration between ad campaign clickers and immediate conversational sales follow-up."
    ],
    deliverables: [
      {
        title: "High-Intent Google Search Ads",
        desc: "Capturing active buyers at the exact moment they search for your service."
      },
      {
        title: "Meta Precision Social Advertising",
        desc: "Behavioral and interest-targeted campaigns on Instagram and Facebook."
      },
      {
        title: "Chat2Customer™ WhatsApp Funnels",
        desc: "Directing ad clicks straight into structured, automated qualifying WhatsApp conversations."
      },
      {
        title: "High-Converting Landing Pages",
        desc: "Single-minded conversion pages designed strictly to eliminate hesitation and drive action."
      },
      {
        title: "Lead Qualification & Pipeline Handoff",
        desc: "Automated screening ensuring sales teams speak only with genuine, budget-fit leads."
      },
      {
        title: "Rigorous CPA & ROAS Optimization",
        desc: "Continuous A/B testing of hooks, copy, creatives, and audiences to lower cost per closed deal."
      }
    ],
    strategicInsight:
      "Through our proprietary Chat2Customer™ system, we bypass friction-heavy web forms and connect interested buyers immediately to verified business dialogues, drastically shortening the sales cycle.",
    bullets: [
      "Lead-generation campaigns",
      "Google Ads",
      "Meta Ads",
      "Campaign creative",
      "Audience strategy",
      "Landing-page recommendations",
      "Lead qualification",
      "Conversion tracking",
      "Remarketing",
      "Campaign optimisation",
      "Performance reporting"
    ],
    relatedServices: ["digital-marketing", "multimedia-production", "ai-powered-marketing"],
    relatedIndustries: ["real-estate", "healthcare", "d2c", "education"]
  },
  {
    slug: "ai-powered-marketing",
    title: "AI-Powered Marketing",
    code: "Service",
    tagline: "AI is not our proposition. It is one of our tools.",
    heroImage: "https://images.unsplash.com/photo-1781127445209-f02336fa36a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    summary:
      "We leverage artificial intelligence not to replace human creativity, but to supercharge speed, scale variation testing, and eliminate operational latency. AI provides velocity; our 20+ years of creative leadership provides the critical judgment and brand taste.",
    painPoints: [
      "Slow turnaround times on creative variations holding back campaign experimentation.",
      "Generic AI-generated copy that feels impersonal, bland, and damaging to brand prestige.",
      "High production costs for large-scale localized or personalized ad creative adaptations.",
      "Underutilized business data and delayed market feedback loops."
    ],
    deliverables: [
      {
        title: "Rapid Creative Variation Testing",
        desc: "Generating dozens of on-brand hook and visual angles to identify top performers fast."
      },
      {
        title: "AI-Assisted Market Research",
        desc: "Accelerated competitive landscape analysis and customer sentiment intelligence."
      },
      {
        title: "Image & Video Enhancement Workflows",
        desc: "High-precision upscaling, background engineering, and dynamic visual adaptation."
      },
      {
        title: "Content Repurposing Engines",
        desc: "Transforming long-form video or core brand thought leadership into multi-platform formats."
      },
      {
        title: "Automated Lead Routing Workflows",
        desc: "Instant CRM integration, categorization, and intelligent notification routing."
      },
      {
        title: "Predictive Campaign Insights",
        desc: "Identifying audience fatigue and bidding opportunities before budgets are wasted."
      }
    ],
    strategicInsight:
      "We adhere strictly to our core principle: AI supplies the computational leverage, while experienced marketing practitioners safeguard the strategy, brand voice, and emotional resonance.",
    bullets: [
      "Research",
      "Brand ideation",
      "Content development",
      "Creative variations",
      "Image enhancement",
      "Video concepts",
      "Content repurposing",
      "Marketing workflows",
      "Campaign analysis",
      "Automation"
    ],
    relatedServices: ["performance-lead-generation", "multimedia-production", "digital-marketing"],
    relatedIndustries: ["d2c", "manufacturing-b2b", "entertainment"]
  }
];

export function getServiceBySlug(slug) {
  if (!slug) return null;
  const clean = slug.toLowerCase().trim();
  return SERVICES.find((s) => s.slug === clean) || null;
}
